import { checkIfIOS } from '@redriverpress/system'
import { useEffect, useRef, useState } from 'react'

export default function useAudioPlayer() {
  const audioNode = useRef(null)

  // NB: `isPlaying` is not a direct reflection of the audio element's play/pause state.
  // We use it to evaluate whether the user has explicitly set the audio to play or pause.
  // Hence, this state value is not updated during the playing/pausing done in the range input's event handlers.
  // We do, however, set this explicitly to false on the audio element's onEnded event.
  const [isPlaying, setIsPlaying] = useState(false)

  // NB: setCurrentTime is not used to set the audio element's actual `currentTime` attribute.
  // We use currentTime to store the value dispatched on the audio element's ontimeupdate event.
  // Hence, we set the audio's currentTime directly within the range input's onChange handler; this in turn
  // causes the audio element's ontimeupdate to fire, which in turn updates our currentTime state value.
  const [currentTime, setCurrentTime] = useState(0)

  const currentMinutes = Math.floor((currentTime / 60) % 60)
  const currentSeconds = Math.floor(currentTime % 60)

  const fallbackDuration = 60 * 2

  const duration = audioNode.current
    ? Math.floor(audioNode.current.duration) || fallbackDuration
    : 0

  const remainingTime = duration - currentTime
  const remainingMinutes = Math.floor((remainingTime / 60) % 60)
  const remainingSeconds = Math.floor(remainingTime % 60)

  const progress = audioNode.current ? (currentTime / duration) * 100 : 0

  // canplaythrough does not fire on iOS because iOS does not preload HTML audio; the user must invoke
  // the audio download manually by hitting the play button :(
  const [canPlayThrough, setCanPlayThrough] = useState(false)

  // canPlayThrough needs to be set on the client, as checkIfIOS requires the navigator interface
  useEffect(() => {
    setCanPlayThrough(checkIfIOS())
  }, [])

  const togglePlay = () => {
    if (isPlaying) {
      setIsPlaying(false)
      return audioNode.current.pause()
    }
    setIsPlaying(true)
    return audioNode.current.play()
  }

  const setTime = time => {
    audioNode.current.currentTime = time
  }

  const handleScrub = () => {
    isPlaying && audioNode.current.play()
  }

  return {
    audioNode,
    canPlayThrough,
    currentTime: {
      value: currentTime,
      minutes: currentMinutes,
      seconds: currentSeconds,
    },
    duration,
    isPlaying,
    progress,
    remainingTime: {
      value: remainingTime,
      minutes: remainingMinutes,
      seconds: remainingSeconds,
    },
    togglePlay,
    setTime,
    handleScrub,
    setCurrentTime,
    setCanPlayThrough,
    setIsPlaying,
  }
}
